/**
 * OnboardingWizard — new-tenant first-login setup assistant.
 *
 * Shown as a floating panel (bottom-right) when:
 *   - Tenant exists (user is not superadmin)
 *   - onboarding_progress.all_steps_done = false
 *   - onboarding_progress.dismissed_at = null
 *
 * Five steps:
 *   1. farm_type      — select primary crop / farm type
 *   2. address        — search + set farm map location
 *   3. first_plant    — has at least 1 plant
 *   4. irrigation     — has at least 1 irrigation zone
 *   5. measurement    — confirm measurement system (auto-detected)
 *
 * Users can dismiss the wizard; it reappears from the checklist
 * widget on the dashboard if they want to revisit it.
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { prefsApi, tenantApi, growableRefApi, varietiesApi, type OnboardingProgress, type UserPreferences, type CropChip } from '../../lib/api'
import { useAuthStore } from '../../lib/store'
import { Scuttle } from '../Scuttle'
import { CropChipPicker } from './CropChipPicker'
import { cropsForLocation } from '../../lib/cropRegions'
import { NarrativeEventCapture } from './NarrativeEventCapture'
import { DocImportModal } from './DocImportModal'
import { useAIConsent } from './AIConsentModal'

// ── Constants ─────────────────────────────────────────────────────────────────

const C = {
  bg:       '#141A0F',
  bgCard:   '#1A2212',
  border:   'rgba(255,255,255,0.09)',
  amber:    '#F5A623', // kept hex: used in SVG progress-ring stroke/fill (can't take var())
  green:    '#2D6A2F',
  greenLight: '#EAF3DE',
  text:     '#F0EDE4',
  textMuted:'#A8A49C',
  textFaint:'#6B6760',
}

const FARM_TYPES = [
  { value: 'mango_orchard',  label: 'Mango orchard',      emoji: '🥭' },
  { value: 'citrus',         label: 'Citrus / lemon / lime', emoji: '🍋' },
  { value: 'avocado',        label: 'Avocado grove',       emoji: '🥑' },
  { value: 'mixed_fruit',    label: 'Mixed fruit farm',    emoji: '🍑' },
  { value: 'food_forest',    label: 'Food forest / guild', emoji: '🌳' },
  { value: 'vineyard',       label: 'Vineyard',            emoji: '🍇' },
  { value: 'general',        label: 'General / other',     emoji: '🌱' },
]

// Farm scale + goals drive the AI planting recommendations (§55.24).
const FARM_SCALES = [
  { value: 'hobby',      label: 'Hobby (<1 ac)' },
  { value: 'small',      label: 'Small (1–10 ac)' },
  { value: 'commercial', label: 'Commercial (10–100 ac)' },
  { value: 'large',      label: 'Large (100+ ac)' },
]
const FARM_GOALS = [
  { value: 'max_yield',    label: 'Maximum yield',       emoji: '📈' },
  { value: 'max_profit',   label: 'Maximum profit',      emoji: '💰' },
  { value: 'personal',     label: 'Personal consumption', emoji: '🧺' },
  { value: 'biodiversity', label: 'Biodiversity',        emoji: '🐝' },
]

const STEPS = ['details', 'farm_type', 'address', 'first_plant', 'irrigation', 'measurement', 'history', 'ai'] as const
type Step = typeof STEPS[number]

function stepDone(progress: OnboardingProgress, step: Step): boolean {
  return (progress as unknown as Record<string, boolean>)[`step_${step}`]
}

function completionCount(progress: OnboardingProgress): number {
  return STEPS.filter(s => stepDone(progress, s)).length
}

// ── Step detail ───────────────────────────────────────────────────────────────

interface StepInfo {
  id: Step
  title: string
  desc: string
  action?: string
  path?: string
}

const STEP_INFO: StepInfo[] = [
  {
    id: 'details', title: 'Your details',
    desc: 'Tell us who runs the farm and where to reach you — your name, your farm’s name, and a mailing address (the farm’s physical location is set on the map step).',
    action: 'Save details below',
  },
  {
    id: 'farm_type', title: 'Set your farm type',
    desc: 'Tell us what you grow so we can tailor suggestions, defaults, and care schedules.',
    action: 'Choose type below',
  },
  {
    id: 'address', title: 'Pin your farm on the map',
    desc: 'Search your address so the map centers on your farm and weather data matches your location.',
    action: 'Open Farm Map', path: '/map',
  },
  {
    id: 'first_plant', title: 'Add your first plant',
    desc: 'Log at least one plant — or upload a farm map / planting plan and let Grafty automatically draft your plants & blocks for review.',
    action: 'Go to Plants', path: '/plants',
  },
  {
    id: 'irrigation', title: 'Set up an irrigation zone',
    desc: 'Connect a controller or draw a manual zone so you can track watering and schedule runs.',
    action: 'Go to Irrigation', path: '/irrigation',
  },
  {
    id: 'measurement', title: 'Confirm measurement system',
    desc: 'We auto-detected your units from your browser — confirm or change to your preference.',
    action: 'Confirm below',
  },
  {
    id: 'history', title: 'Record your farm history',
    desc: 'Type or paste what you\'ve done — going back as far as you remember. Grafty will turn a long note (or a pasted spreadsheet) into individual dated events. You can also do this any time from the Activity page.',
    action: 'Add events below',
  },
  {
    id: 'ai', title: 'Turn on Grafty AI',
    desc: 'Enable AI features — narrative event capture, plant-problem diagnosis, ripening and yield insights. You can review the terms and turn this on now, or skip and enable later in Settings.',
    action: 'Review & enable below',
  },
]

// ── Main component ─────────────────────────────────────────────────────────────

export function OnboardingWizard() {
  const { role } = useAuthStore()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [expanded, setExpanded] = useState(true)
  const [activeStep, setActiveStep] = useState<Step>('details')
  const [showDocImport, setShowDocImport] = useState(false)
  const [selectedFarmType, setSelectedFarmType] = useState('')
  const [onboardCrops, setOnboardCrops] = useState<CropChip[]>([])
  const [selectedScale, setSelectedScale] = useState('')
  const [selectedGoals, setSelectedGoals] = useState<string[]>([])
  const [selectedMeasurement, setSelectedMeasurement] = useState<'imperial' | 'metric'>('imperial')
  // "Your details" step fields (formal name, farm name, mailing address).
  const [legalName, setLegalName] = useState('')
  const [detFarmName, setDetFarmName] = useState('')
  const [legalBiz, setLegalBiz] = useState('')
  const [phone, setPhone] = useState('')
  const [sameAsFarm, setSameAsFarm] = useState(true)
  const [mStreet, setMStreet] = useState('')
  const [mCity, setMCity] = useState('')
  const [mState, setMState] = useState('')
  const [mPostal, setMPostal] = useState('')
  const [mCountry, setMCountry] = useState('US')
  // Let Settings → "Re-open setup guide" force the wizard back open even after
  // it's been dismissed or all steps are done.
  const [forceOpen, setForceOpen] = useState(false)
  useEffect(() => {
    const open = () => { setForceOpen(true); setExpanded(true) }
    window.addEventListener('grafty:open-setup', open)
    return () => window.removeEventListener('grafty:open-setup', open)
  }, [])

  // Show only for users who actually belong to a tenant farm. Superusers
  // (staff console, no tenant) and users who haven't joined/created a tenant
  // yet (pending_tenant) or are mid-MFA don't onboard a farm. We gate on role
  // — NOT on a hydrated tenant object — because the auth store only carries
  // tenant_id at login, never a full Tenant object, so `tenant` is always null
  // here and would suppress the wizard for every real tenant member.
  // Hooks must run unconditionally (rules-of-hooks) — do NOT early-return on role here.
  // Compute eligibility, gate the data fetches with it, and return null AFTER all hooks
  // (with the other guards below). Superusers / pre-tenant / mid-MFA don't onboard a farm.
  const onboardingRoleOK = !!role && role !== 'superuser' && role !== 'pending_tenant' && role !== 'mfa_pending'

  // Growable reference for the wizard's crop pickers. MUST be gated on
  // onboardingRoleOK: this hits the AUTHED /varieties/reference endpoint, and the
  // wizard is mounted app-wide (not inside RequireAuth), so a logged-out visitor
  // would otherwise fire it → 401 → api.ts interceptor hardLogout() →
  // window.location='/login' full reload → wizard remounts → fires again → an
  // infinite 401/reload loop (the "reference is 401 in a constant loop, even
  // logged out / incognito" bug). Empty role ⇒ disabled ⇒ never fires.
  const { data: growableList } = useQuery({
    queryKey: ['growable-ref'],
    queryFn: () => growableRefApi.list().then(r => r.data.reference ?? []),
    staleTime: 5 * 60_000,
    enabled: onboardingRoleOK,
  })

  const { data: onboardingData } = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => prefsApi.getOnboarding().then(r => r.data),
    enabled: onboardingRoleOK,
  })

  const { data: myPrefs } = useQuery({
    queryKey: ['my-prefs'],
    queryFn: () => prefsApi.get().then(r => r.data),
    enabled: onboardingRoleOK,
  })
  useEffect(() => {
    if (myPrefs?.measurement_system) setSelectedMeasurement(myPrefs.measurement_system)
  }, [myPrefs])

  const completeStep = useMutation({
    mutationFn: (step: Step) => prefsApi.completeStep(step),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding'] }),
  })

  const saveDetails = useMutation({
    mutationFn: () => prefsApi.saveDetails({
      legal_name: legalName.trim(),
      farm_name: detFarmName.trim(),
      legal_business_name: legalBiz.trim(),
      contact_phone: phone.trim(),
      mailing_same_as_farm: sameAsFarm,
      mailing_street: mStreet.trim(),
      mailing_city: mCity.trim(),
      mailing_state: mState.trim(),
      mailing_postal: mPostal.trim(),
      mailing_country: mCountry.trim(),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['onboarding'] }); setActiveStep('farm_type') },
  })

  // AI master consent (ai_general) — the wizard "ai" step enables it.
  const aiConsent = useAIConsent('ai_general')
  const aiAlreadyOn = !!(aiConsent.status && !aiConsent.status.consent_required)

  const dismiss = useMutation({
    mutationFn: () => prefsApi.dismissOnboarding(),
  })

  const updatePrefs = useMutation({
    mutationFn: (fields: Partial<UserPreferences>) => prefsApi.update(fields),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-prefs'] }),
  })

  if (!onboardingRoleOK) return null // superuser / pre-tenant / mid-MFA — no farm onboarding
  if (!onboardingData) return null
  if (!forceOpen && onboardingData.all_steps_done) return null
  if (!forceOpen && onboardingData.dismissed_at) return null

  const done = completionCount(onboardingData)
  const total = STEPS.length
  const pct = Math.round((done / total) * 100)

  const info = STEP_INFO.find(s => s.id === activeStep)!
  const isCurrentDone = stepDone(onboardingData, activeStep)

  async function handleFarmTypeSubmit() {
    if (!selectedFarmType) return
    // Persist farm type + scale + goals via the dedicated partial-safe endpoint (§55.24) —
    // these feed the AI planting recommendations (Reports → Insights). Best-effort: never
    // block onboarding if the save fails. (Not PUT /tenant — a partial payload there would
    // blank the rest of the profile.)
    try {
      await tenantApi.saveOnboardingProfile({
        farm_type: selectedFarmType,
        farm_scale: selectedScale || undefined,
        farm_goals: selectedGoals.length ? selectedGoals : undefined,
      })
      qc.invalidateQueries({ queryKey: ['tenant-profile'] })
    } catch { /* non-blocking */ }
    // Materialize the chosen crops into varieties (best-effort — never block onboarding).
    if (onboardCrops.length) {
      try { await varietiesApi.fromCrops(onboardCrops); qc.invalidateQueries({ queryKey: ['varieties'] }) } catch { /* non-blocking */ }
    }
    completeStep.mutate('farm_type')
    setActiveStep('address')
  }
  function toggleGoal(v: string) {
    setSelectedGoals(g => g.includes(v) ? g.filter(x => x !== v) : [...g, v])
  }

  function handleMeasurementSubmit() {
    updatePrefs.mutate({ measurement_system: selectedMeasurement })
    completeStep.mutate('measurement')
  }

  function handleNavigate() {
    if (info.path) {
      navigate(info.path)
      setExpanded(false)
    }
  }

  if (!expanded) {
    // Collapsed pill
    return (
      <div
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 9998,
          background: C.bg, border: `1px solid ${C.border}`,
          borderRadius: 100, padding: '4px 16px 4px 6px',
          display: 'flex', alignItems: 'center', gap: 10,
          cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        }}
        onClick={() => setExpanded(true)}
        role="button"
        aria-label="Open onboarding wizard"
      >
        <Scuttle size={34} showSubtitle={false} mood={done === total ? 'success' : 'cute'} />
        <div style={{ position: 'relative', width: 28, height: 28 }}>
          <svg viewBox="0 0 28 28" width={28} height={28}>
            <circle cx={14} cy={14} r={12} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={2.5} />
            <circle cx={14} cy={14} r={12} fill="none" stroke={C.amber} strokeWidth={2.5}
              strokeDasharray={`${75.4 * pct / 100} 75.4`}
              strokeLinecap="round" transform="rotate(-90 14 14)" />
          </svg>
          <span style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, fontWeight: 700, color: C.amber,
          }}>{pct}%</span>
        </div>
        <span style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>Setup</span>
        <span style={{ fontSize: 11, color: C.textMuted }}>{done}/{total}</span>
      </div>
    )
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false) }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, fontFamily: '"DM Sans", system-ui, sans-serif',
      }}>
      <div style={{
        width: 'min(960px, 66vw)', maxWidth: '96vw', height: 'min(660px, 84vh)',
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 18,
        boxShadow: '0 18px 64px rgba(0,0,0,0.65)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Hero banner */}
        <div style={{
          position: 'relative', display: 'flex', alignItems: 'center', gap: 18,
          padding: '18px 22px', borderBottom: `1px solid ${C.border}`,
          background: 'linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 12%, transparent), rgba(45,106,47,0.12))',
        }}>
          <div style={{ flexShrink: 0 }} title="Scuttle, Chief Morale Officer">
            <Scuttle size={82} showSubtitle={false} mood={done === total ? 'success' : 'cute'} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 21, fontWeight: 800, color: C.text, fontFamily: '"Syne", system-ui, sans-serif', lineHeight: 1.15 }}>
              {done === total ? 'Your farm is all set 🎉' : 'Welcome to Grafty — let’s set up your farm'}
            </div>
            <div style={{ fontSize: 13, color: C.textMuted, marginTop: 5 }}>
              {done} of {total} steps done · Scuttle will walk you through it
            </div>
          </div>
          <div style={{ position: 'absolute', top: 12, right: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
            <svg viewBox="0 0 36 36" width={42} height={42}>
              <circle cx={18} cy={18} r={15} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={3} />
              <circle cx={18} cy={18} r={15} fill="none" stroke={C.amber} strokeWidth={3}
                strokeDasharray={`${94.25 * pct / 100} 94.25`}
                strokeLinecap="round" transform="rotate(-90 18 18)" />
              <text x={18} y={22} textAnchor="middle" fontSize={9} fontWeight={700} fill={C.amber}>{pct}%</text>
            </svg>
            <button onClick={() => setExpanded(false)} title="Minimize"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: C.textFaint, fontSize: 18 }}>—</button>
            <button onClick={() => { dismiss.mutate() }} title="Dismiss wizard"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: C.textFaint, fontSize: 16 }}>✕</button>
          </div>
        </div>

        {/* Body: step list (left) + active detail (right) */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

      {/* Step list */}
      <div style={{ padding: '12px', width: 270, flexShrink: 0, borderRight: `1px solid ${C.border}`, overflowY: 'auto' }}>
        {STEP_INFO.map((s) => {
          const isDone = stepDone(onboardingData, s.id)
          const isActive = activeStep === s.id
          return (
            <button
              key={s.id}
              onClick={() => setActiveStep(s.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '7px 8px', borderRadius: 8,
                background: isActive ? 'color-mix(in srgb, var(--color-primary) 8%, transparent)' : 'transparent',
                border: isActive ? `1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)` : '1px solid transparent',
                cursor: 'pointer', textAlign: 'left', marginBottom: 2,
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                background: isDone ? C.green : isActive ? 'color-mix(in srgb, var(--color-primary) 15%, transparent)' : 'rgba(255,255,255,0.06)',
                border: isDone ? 'none' : isActive ? `1.5px solid ${C.amber}` : '1.5px solid rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isDone
                  ? <span style={{ fontSize: 11, color: '#fff' }}>✓</span>
                  : <span style={{ fontSize: 10, color: isActive ? C.amber : C.textFaint }}>
                      {STEPS.indexOf(s.id) + 1}
                    </span>
                }
              </div>
              <span style={{ fontSize: 12, color: isDone ? C.textMuted : isActive ? C.text : C.textMuted, fontWeight: isActive ? 500 : 400 }}>
                {s.title}
              </span>
            </button>
          )
        })}
      </div>

      {/* Active step detail (right column) */}
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '18px 22px' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>{info.title}</div>
        <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6, marginBottom: 16, maxWidth: 460 }}>{info.desc}</div>

        {/* Your details — formal name, farm name, mailing address */}
        {activeStep === 'details' && !isCurrentDone && (() => {
          const fld: React.CSSProperties = {
            width: '100%', padding: '8px 10px', borderRadius: 8, marginBottom: 8,
            border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.05)',
            color: C.text, fontSize: 12,
          }
          const valid = legalName.trim().length >= 2 && detFarmName.trim().length >= 2
          return (
            <div style={{ maxWidth: 460 }}>
              <input style={fld} value={legalName} onChange={e => setLegalName(e.target.value)}
                placeholder="Your full legal name *" autoComplete="name" />
              <input style={fld} value={detFarmName} onChange={e => setDetFarmName(e.target.value)}
                placeholder="Farm name *" autoComplete="organization" />
              <input style={fld} value={legalBiz} onChange={e => setLegalBiz(e.target.value)}
                placeholder="Legal business name (optional)" />
              <input style={fld} value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="Contact phone (optional)" autoComplete="tel" inputMode="tel" />

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0', fontSize: 12, color: C.textMuted, cursor: 'pointer' }}>
                <input type="checkbox" checked={sameAsFarm} onChange={e => setSameAsFarm(e.target.checked)} />
                Mailing address is the same as my farm address
              </label>

              {!sameAsFarm && (
                <>
                  <input style={fld} value={mStreet} onChange={e => setMStreet(e.target.value)}
                    placeholder="Mailing street address" autoComplete="street-address" />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...fld, flex: 2 }} value={mCity} onChange={e => setMCity(e.target.value)} placeholder="City" />
                    <input style={{ ...fld, flex: 1 }} value={mState} onChange={e => setMState(e.target.value)} placeholder="State" />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...fld, flex: 1 }} value={mPostal} onChange={e => setMPostal(e.target.value)} placeholder="ZIP / postal" inputMode="numeric" />
                    <input style={{ ...fld, flex: 1 }} value={mCountry} onChange={e => setMCountry(e.target.value)} placeholder="Country" autoComplete="country" />
                  </div>
                </>
              )}

              <button
                onClick={() => saveDetails.mutate()}
                disabled={!valid || saveDetails.isPending}
                style={{
                  width: '100%', marginTop: 4, padding: '8px', borderRadius: 8, border: 'none',
                  background: valid ? C.green : 'rgba(255,255,255,0.08)', color: '#fff',
                  fontSize: 12, fontWeight: 500, cursor: valid ? 'pointer' : 'not-allowed',
                }}
              >{saveDetails.isPending ? 'Saving…' : 'Save details'}</button>
            </div>
          )
        })()}

        {/* Farm type selector */}
        {activeStep === 'farm_type' && !isCurrentDone && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
              {FARM_TYPES.map(ft => (
                <button
                  key={ft.value}
                  onClick={() => setSelectedFarmType(ft.value)}
                  style={{
                    padding: '8px 6px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: selectedFarmType === ft.value ? 'color-mix(in srgb, var(--color-primary) 15%, transparent)' : 'rgba(255,255,255,0.05)',
                    outline: selectedFarmType === ft.value ? `1.5px solid ${C.amber}` : '1.5px solid transparent',
                    color: C.text, fontSize: 11, textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 16, marginBottom: 3 }}>{ft.emoji}</div>
                  {ft.label}
                </button>
              ))}
            </div>

            {/* Farm scale (optional) — feeds AI planting recommendations (§55.24) */}
            <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 5 }}>Farm scale (optional)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
              {FARM_SCALES.map(fs => (
                <button
                  key={fs.value}
                  onClick={() => setSelectedScale(s => s === fs.value ? '' : fs.value)}
                  style={{
                    padding: '7px 6px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: selectedScale === fs.value ? 'color-mix(in srgb, var(--color-primary) 15%, transparent)' : 'rgba(255,255,255,0.05)',
                    outline: selectedScale === fs.value ? `1.5px solid ${C.amber}` : '1.5px solid transparent',
                    color: C.text, fontSize: 11, textAlign: 'center',
                  }}
                >{fs.label}</button>
              ))}
            </div>

            {/* Primary goals (optional, multi-select) — feeds AI planting recommendations */}
            <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 5 }}>Primary goals (optional — pick any)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
              {FARM_GOALS.map(g => (
                <button
                  key={g.value}
                  onClick={() => toggleGoal(g.value)}
                  style={{
                    padding: '7px 6px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: selectedGoals.includes(g.value) ? 'color-mix(in srgb, var(--color-primary) 15%, transparent)' : 'rgba(255,255,255,0.05)',
                    outline: selectedGoals.includes(g.value) ? `1.5px solid ${C.amber}` : '1.5px solid transparent',
                    color: C.text, fontSize: 11, textAlign: 'center',
                  }}
                >
                  <span style={{ marginRight: 4 }}>{g.emoji}</span>{g.label}
                </button>
              ))}
            </div>

            {/* Crops — chip picker; materialized into varieties on continue. */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 5 }}>What do you grow? (optional)</div>
              <CropChipPicker
                value={onboardCrops}
                onChange={setOnboardCrops}
                suggestions={(growableList || []).map(g => ({ id: g.id, common_name: g.common_name }))}
                topCrops={cropsForLocation(`${mCity} ${mState} ${mPostal}`)}
                onProposeCustom={async (name) => { try { return (await growableRefApi.propose(name)).data.id } catch { return undefined } }}
              />
              <div style={{ fontSize: 10, color: C.textFaint, marginTop: 6 }}>
                You can add more crops (and manage varieties) anytime from the Varieties page.
              </div>
            </div>

            <button
              onClick={handleFarmTypeSubmit}
              disabled={!selectedFarmType}
              style={{
                width: '100%', padding: '8px', borderRadius: 8, border: 'none',
                background: selectedFarmType ? C.green : 'rgba(255,255,255,0.08)',
                color: '#fff', fontSize: 12, cursor: selectedFarmType ? 'pointer' : 'not-allowed',
                fontWeight: 500,
              }}
            >Save & continue</button>
          </>
        )}

        {/* Measurement system selector */}
        {activeStep === 'measurement' && !isCurrentDone && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {(['imperial', 'metric'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setSelectedMeasurement(m)}
                  style={{
                    flex: 1, padding: '10px 8px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: selectedMeasurement === m ? 'color-mix(in srgb, var(--color-primary) 15%, transparent)' : 'rgba(255,255,255,0.05)',
                    outline: selectedMeasurement === m ? `1.5px solid ${C.amber}` : '1.5px solid transparent',
                    color: C.text, fontSize: 11,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>
                    {m === 'imperial' ? '🇺🇸 Imperial' : '🌍 Metric'}
                  </div>
                  <div style={{ color: C.textFaint }}>
                    {m === 'imperial' ? 'lbs · acres · °F · gal' : 'kg · ha · °C · L'}
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={handleMeasurementSubmit}
              style={{
                width: '100%', padding: '8px', borderRadius: 8, border: 'none',
                background: C.green, color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 500,
              }}
            >Confirm measurement system</button>
          </>
        )}

        {/* Farm-history narrative capture */}
        {activeStep === 'history' && !isCurrentDone && (
          <>
            <NarrativeEventCapture onCommitted={() => completeStep.mutate('history')} />
            <button
              onClick={() => completeStep.mutate('history')}
              style={{
                width: '100%', marginTop: 10, padding: '8px', borderRadius: 8,
                border: `1px solid ${C.border}`, background: 'transparent',
                color: C.textMuted, fontSize: 12, cursor: 'pointer',
              }}
            >Done — I'll add more later</button>
          </>
        )}

        {/* AI enablement (master consent) */}
        {activeStep === 'ai' && !isCurrentDone && (
          <>
            {aiAlreadyOn && (
              <div style={{ fontSize: 11, color: C.green, marginBottom: 8 }}>✓ AI is already enabled for your farm.</div>
            )}
            <button
              onClick={() => aiConsent.requireConsent().then(() => completeStep.mutate('ai')).catch(() => {})}
              style={{
                width: '100%', padding: '8px', borderRadius: 8, border: 'none',
                background: C.amber, color: '#1a1300', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >{aiAlreadyOn ? 'Review AI terms' : 'Review & enable AI'}</button>
            <button
              onClick={() => completeStep.mutate('ai')}
              style={{
                width: '100%', marginTop: 8, padding: '8px', borderRadius: 8,
                border: `1px solid ${C.border}`, background: 'transparent',
                color: C.textMuted, fontSize: 12, cursor: 'pointer',
              }}
            >Not now — I'll enable in Settings</button>
          </>
        )}

        {/* First-plant — upload a farm map / planting plan for AI extraction */}
        {activeStep === 'first_plant' && !isCurrentDone && (
          <div style={{ marginBottom: 12 }}>
            <button
              onClick={() => setShowDocImport(true)}
              style={{
                width: '100%', padding: '10px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 12,
                background: 'color-mix(in srgb, var(--color-primary) 12%, transparent)', border: `1px solid color-mix(in srgb, var(--color-primary) 33%, transparent)`, color: C.amber,
              }}
            >📄 Upload a farm map or planting plan</button>
            <div style={{ fontSize: 11, color: C.textFaint, marginTop: 6, lineHeight: 1.5 }}>
              Grafty automatically reads it and drafts your plants &amp; blocks for review — or add them by hand below.
            </div>
          </div>
        )}
        {showDocImport && (
          <DocImportModal
            onClose={() => setShowDocImport(false)}
            onImported={() => { setShowDocImport(false); completeStep.mutate('first_plant') }}
          />
        )}

        {/* Navigate step */}
        {info.path && !isCurrentDone && (
          <button
            onClick={handleNavigate}
            style={{
              width: '100%', padding: '8px', borderRadius: 8, border: 'none',
              background: C.green, color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 500,
            }}
          >{info.action}</button>
        )}

        {/* Already done */}
        {isCurrentDone && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 10px', background: 'rgba(45,106,47,0.15)',
            borderRadius: 8, border: '1px solid rgba(45,106,47,0.3)',
          }}>
            <span style={{ color: C.green, fontSize: 14 }}>✓</span>
            <span style={{ fontSize: 12, color: '#86c98a' }}>Step complete</span>
          </div>
        )}
      </div>{/* /active detail (right column) */}
        </div>{/* /body row */}

        {/* AI consent modal (triggered by the AI step) */}
        <aiConsent.ConsentModal />
      </div>{/* /panel */}
    </div>
  )
}
